/**
 * Outils › Scribe (outils.scribe, lot 1 du 30/10/2026) – règles partagées API / front de l'enregistreur de réunions :
 * le navigateur enregistre la réunion par parties de 10 s (Opus), déposées dans S3 au fil de l'eau ; à l'arrêt, le
 * serveur transcrit (Mistral Voxtral : langue détectée, orateurs séparés) puis l'IA du socle rédige un compte-rendu que
 * l'auteur relit et valide. « Scribe » ne se traduit pas. Cadrage : docs/SCRIBE.md.
 * Valeurs identiques aux contraintes de la migration db/migrations/outils/20261030_1500_outils_scribe.sql.
 */

export const RESSOURCE_SCRIBE = 'outils.scribe';
export const ROUTE_SCRIBE = '/outils/scribe';

/**
 * État d'une réunion : `enregistrement` (parties en cours de dépôt), `traitement` (arrêtée : transcription puis
 * compte-rendu sur le serveur), `a-valider` (compte-rendu proposé, modifiable), `validee` (figée), `erreur`.
 */
export const ETATS_REUNION = ['enregistrement', 'traitement', 'a-valider', 'validee', 'erreur'] as const;
export type EtatReunion = (typeof ETATS_REUNION)[number];

/**
 * Erreur du compte-rendu (la transcription, elle, a réussi) : la réunion garde sa transcription et son auteur peut
 * « Relancer le compte-rendu » (incident du 02/11/2026 : plus aucune réunion perdue sur un échec du compte-rendu).
 */
export const ERREUR_COMPTE_RENDU = 'compte_rendu_echec';

/** Niveau de détail du compte-rendu (consignes de l'exe Scribe) : Détaillé par défaut (décision du 30/10/2026). */
export const NIVEAUX_COMPTE_RENDU = ['essentiel', 'detaille', 'exhaustif'] as const;
export type NiveauCompteRendu = (typeof NIVEAUX_COMPTE_RENDU)[number];
export const NIVEAU_COMPTE_RENDU_DEFAUT: NiveauCompteRendu = 'detaille';

/**
 * Audio accepté pour une partie : Opus de MediaRecorder (Edge, Chrome : WebM ; Firefox : Ogg), deux formats que
 * Voxtral transcrit sans conversion. Safari (MP4/AAC) : lot 2.
 */
export const TYPES_PARTIE_SCRIBE = ['audio/webm', 'audio/ogg'] as const;
export type TypePartieScribe = (typeof TYPES_PARTIE_SCRIBE)[number];

/** Une partie toutes les 10 s : un rechargement de la page ne fait perdre que la partie en cours. */
export const DUREE_PARTIE_MS = 10_000;
/** 10 s d'Opus pèsent environ 40 Ko : large marge pour un débit élevé. */
export const TAILLE_MAX_PARTIE = 1024 * 1024;
/**
 * Séquence = un enregistrement continu (un fichier audio complet à lui seul), transcrit dès qu'il se termine. Le
 * navigateur en ouvre une nouvelle toutes les 10 min (décision du 30/10/2026 ; Voxtral transcrit au plus 60 min par
 * requête), et à chaque reprise (rechargement, micro changé).
 */
export const DUREE_SEQUENCE_MS = 10 * 60_000;
/** Réunion de 8 h au plus (au-delà, une nouvelle réunion) : 48 séquences, plus les reprises. */
export const DUREE_MAX_REUNION_S = 8 * 3600;
export const SEQUENCES_MAX = 100;
/** 60 parties de 10 s par séquence, et une marge (parties un peu plus courtes, au début et à la fin). */
export const PARTIES_PAR_SEQUENCE_MAX = Math.ceil(DUREE_SEQUENCE_MS / DUREE_PARTIE_MS) + 10;

/** Plafond d'usage : 10 h enregistrées par semaine et par personne (semaine du lundi, heure de Paris). */
export const PLAFOND_SEMAINE_S = 10 * 3600;

export const BORNES_SCRIBE = {
  titre: 200,
  resume: 20_000,
  /** Section du compte-rendu (Détaillé, Exhaustif) : un sujet abordé et son déroulé. */
  section: 20_000,
  sections: 50,
  element: 2_000,
  elements: 100,
  responsable: 200,
  /** Nom cité dans la réunion : participant nommé, organisation citée. */
  nomCite: 120,
  /** Participants nommés, organisations citées : au plus 30 de chaque. */
  nomsCites: 30,
  /** Invités de la réunion Outlook gardés au démarrage (lot 3 « agenda Outlook ») : 100 au plus, comme les destinataires d'un e-mail. */
  invites: 100,
  /** Participants du compte-rendu : invités d'Outlook et personnes nommées dans la réunion, réunis. */
  participants: 100,
  /** Titre proposé par l'IA quand l'auteur a laissé le titre par défaut. */
  titreIa: 80,
} as const;

/**
 * Agenda Outlook (lot 3, 02/11/2026) : réunions de l'utilisateur en cours ou qui commencent dans les 15 minutes,
 * proposées dans le panneau au démarrage (titre et invités), lues par Microsoft Graph avec le droit délégué
 * Calendars.Read de sa connexion à la messagerie ; rien de l'agenda n'est gardé.
 */
export const FENETRE_AGENDA_MIN = 15;
export const EVENEMENTS_AGENDA_MAX = 5;

/**
 * Agenda vu du panneau : `disponible` (liste, éventuellement vide), `messagerie_non_connectee` (aucune connexion à la
 * messagerie Microsoft 365, ou messagerie non configurée : rien n'est montré), `agenda_inaccessible` (connexion sans le
 * droit de lire l'agenda : reconnecter la messagerie).
 */
export const ETATS_AGENDA = ['disponible', 'messagerie_non_connectee', 'agenda_inaccessible'] as const;
export type EtatAgenda = (typeof ETATS_AGENDA)[number];
export const TYPES_INVITE_AGENDA = ['organisateur', 'requis', 'facultatif'] as const;
export type TypeInviteAgenda = (typeof TYPES_INVITE_AGENDA)[number];

/** Clé de comparaison d'un nom cité : casse, accents et espaces ignorés (« Méthanisation  du nord » = « Methanisation du Nord »). */
export const cleNomCite = (nom: string) => nom.normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Noms cités dans une réunion (participants, organisations), tels que relevés par l'IA ou saisis : espaces resserrés,
 * entrées vides et doublons retirés (casse et accents ignorés, première écriture gardée), chaque nom borné à
 * BORNES_SCRIBE.nomCite caractères, au plus `max` noms (BORNES_SCRIBE.nomsCites par défaut).
 */
export function nomsCites(noms: readonly string[], max: number = BORNES_SCRIBE.nomsCites): string[] {
  const vus = new Set<string>();
  const sortie: string[] = [];
  for (const brut of noms) {
    // Une demi-paire UTF-16 laissée par la coupe est retirée.
    const nom = brut.replace(/\s+/g, ' ').trim().slice(0, BORNES_SCRIBE.nomCite).replace(/[\uD800-\uDBFF]$/, '').trim();
    const cle = cleNomCite(nom);
    if (!cle || vus.has(cle)) continue;
    vus.add(cle);
    sortie.push(nom);
    if (sortie.length === max) break;
  }
  return sortie;
}

/** Invité d'une réunion Outlook (nom affiché, adresse). */
export interface InviteNomme { nom: string; email: string }

/** Nom d'un invité tel qu'écrit dans le compte-rendu : son nom affiché, sinon son adresse. */
export const nomInvite = (i: InviteNomme) => i.nom.trim() || i.email;

/**
 * Participants du compte-rendu : les invités de la réunion Outlook choisie au démarrage, puis les personnes nommées dans
 * la réunion, sans doublon (casse et accents ignorés : nomsCites), au plus BORNES_SCRIBE.participants. Même règle pour
 * l'API (traitement) et la maquette.
 */
export function participantsReunion(invites: readonly InviteNomme[], nommes: readonly string[]): string[] {
  return nomsCites([...invites.map(nomInvite), ...nommes], BORNES_SCRIBE.participants);
}

/**
 * Titre proposé par l'IA → titre de la réunion : espaces resserrés, guillemets d'encadrement retirés, borné à
 * BORNES_SCRIBE.titreIa caractères (sans demi-paire UTF-16) ; null s'il ne reste rien.
 */
export function titrePropose(brut: string | null | undefined): string | null {
  const t = (brut ?? '').replace(/\s+/g, ' ').trim().replace(/^["'«“‘\s]+|["'»”’\s]+$/g, '').trim();
  return t.slice(0, BORNES_SCRIBE.titreIa).replace(/[\uD800-\uDBFF]$/, '').trim() || null;
}
